/**
 * Custom pills for filters control select store
 *
 * @module views/components/FilterPill
 * @memberof -Common
 */

import React from 'react';

import PropTypes from 'prop-types';

import FilterHeader from '../FilterHeader/FilterHeader';
import SelectStore from '../SelectStore/SelectStore';
import StoreFilter from '../StoreFilter/StoreFilter';

/**
 * Represents a FilterPill component
 *
 * @method
 * @param {FilterPillProps} props - React properties passed from composition
 * @returns FilterPill
 */
export const FilterPill = ( props ) => {
  if( !props ){
    return;
  }

  switch ( props.type ){
    case 'FilterHeader':
      return (
        <FilterHeader
          filterbuttonArialabel={ props.filterbuttonArialabel }
          iconImage={ props.iconImage }
          filterbuttonLabel={ props.showFilterLabel }
          onFilterClick={ props.onPillClick }
          ariaExpanded={ props.ariaExpanded }
          search={ props.search }
          isMobileDevice={ props.isMobileDevice }
          showFiltersButtonRef={ props.showFiltersButtonRef }
          isSelectedStore={ props.isSelectedStore }
          isStoreChecked={ props.isStoreChecked }
          filterByStorePill={ props.filterByStorePill }
        />
      );
    case 'StoreFilter':
      return (
        <StoreFilter
          iconImage={ props.iconImage }
          closeIcon={ props.closeIcon }
          key={ props.name }
          message={ props.name }
          onClick={ props.onClick }
          selectAction={ props.selectAction }
          isSelectedStore={ props.isSelectedStore }
          isStoreChecked={ props.isStoreChecked }
        />
      );
    case 'SelectStore':
      return (
        <SelectStore
          iconImage={ props.iconImage }
          label={ props.name }
          selectAction={ props.selectAction }
          isSelectedStore={ props.isSelectedStore }
        />
      );
    default:
      return null;
  }
};

/**
 * Property type definitions
 * @typedef FilterPillProps
 * @type {object}
 * @property {string} filterbuttonLabel - Sets the label for the filter button.
 * @property {string} filterbuttonArialabel - Sets the aria label for the filter button.
 * @property {boolean} ariaExpanded - Sets the aria-expanded property for the filter button.
 * @property {string} search - Gets the value of the search filter category.
 * @property {object} showFiltersButtonRef - Reference for the show filters button.
 * @property {string} iconImage - Icon image URL or identifier for the component.
 * @property {string} closeIcon - Close icon for the StoreFilter component.
 * @property {string} name - Name or label of the filter.
 * @property {function} onClick - Function to handle click events on the component.
 * @property {object} selectAction - Function to handle selection actions in SelectStore.
 * @property {object} isSelectedStore - Contains selected store
 * @property {boolean} isStoreChecked - Check if store is checked
 * @property {string} label - Label for the SelectStore component.
 */
export const propTypes = {
  /** Sets the label for the filter button */
  filterbuttonLabel: PropTypes.string,
  /** Sets the aria label for the filter button */
  filterbuttonArialabel: PropTypes.string,
  /** Onclick function */
  onFilterClick: PropTypes.func,
  /** Aria-expanded property */
  ariaExpanded: PropTypes.bool,
  /** Sets the filter category value */
  search: PropTypes.string,
  /** Checks current device */
  isMobileDevice: PropTypes.bool,
  /** Flyout opener ref */
  showFiltersButtonRef: PropTypes.object,
  /** Icon image for the component */
  iconImage: PropTypes.string,
  /** Close icon for the StoreFilter component */
  closeIcon: PropTypes.string,
  /** Name or label of the filter */
  name: PropTypes.string,
  /** Click handler for the component */
  onClick: PropTypes.func,
  /** Select action function for SelectStore */
  selectAction: PropTypes.object,
  /** Label for the SelectStore component */
  label: PropTypes.string,
  /** Contains selected store */
  isSelectedStore: PropTypes.object,
  /** Check if store is checked */
  isStoreChecked: PropTypes.bool
};

FilterPill.propTypes = propTypes;

export default FilterPill;